import { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { Button } from "../ui/button"
import { Input } from "../ui/input"
import { Label } from "../ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card"
import { Alert, AlertDescription } from "../ui/alert"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select"
import { Eye, EyeOff, Lock, Mail, Brain, Building2 } from "lucide-react"
import { Link, useNavigate } from "react-router-dom"
import { useAuth } from "../../contexts/auth-context"
import { apiService } from "../../lib/api"
import { formatErrorMessage } from "../../lib/utils"

const loginSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
})

type LoginFormData = z.infer<typeof loginSchema>

export function LoginForm() {
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")
  const [showCompanySelection, setShowCompanySelection] = useState(false)
  const [companies, setCompanies] = useState<any[]>([])
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>("")
  const [loadingCompanies, setLoadingCompanies] = useState(false)
  const { login } = useAuth()
  const navigate = useNavigate()

  const {
    register: registerLogin,
    handleSubmit: handleLoginSubmit,
    formState: { errors: loginErrors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  })

  // Helper function to decode JWT payload
  const parseJwt = (token: string) => {
    try {
      const base64Url = token.split('.')[1]
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      )
      return JSON.parse(jsonPayload)
    } catch (error) {
      console.error('Error parsing JWT:', error)
      return null
    }
  }

  // Fetch companies after successful login
  const fetchUserCompanies = async () => {
    setLoadingCompanies(true)
    try {
      // Check if user is admin from JWT token
      const token = localStorage.getItem('auth_token')
      let isAdmin = false
      
      if (token) {
        const payload = parseJwt(token)
        const userRoles = payload?.roles || []
        isAdmin = Array.isArray(userRoles) && userRoles.includes('admin')
      }

      // If user is admin, skip company selection and go directly to dashboard
      if (isAdmin) {
        // Optionally set first company if available, but not required for admins
        try {
          const response = await apiService.getCompanies({ page: 1, pageSize: 1 })
          const companiesArray = Array.isArray(response?.data) ? response.data : []
          
          // Set first company if available, but proceed anyway
          if (companiesArray.length > 0) {
            const company = companiesArray[0]
            localStorage.setItem("company_id", company.id)
            if (company.name) {
              localStorage.setItem("company_name", company.name)
            }
          }
        } catch (err) {
          console.log("Could not fetch companies for admin, proceeding anyway")
        }
        
        navigate("/dashboard")
        return
      }

      // For non-admin users, fetch companies and show selection
      const response = await apiService.getCompanies({ page: 1, pageSize: 100 })
      console.log("📦 Companies API Response:", response)
      
      // Handle response structure
      const allCompanies = Array.isArray(response?.data) ? response.data : []
      
      // Filter out inactive companies - only show active companies for login
      const activeCompanies = allCompanies.filter((company: any) => {
        // Check status from company object or companySettings
        const status = company.status || 
                      company.companySettings?.find((s: any) => s.key === 'status')?.value ||
                      'active';
        return status === 'active' || status === 'ACTIVE';
      });
      
      console.log("📦 Active companies:", activeCompanies)
      setCompanies(activeCompanies)
      
      // If no active companies, show error
      if (activeCompanies.length === 0) {
        setError("You don't have access to any active companies. All your assigned companies appear to be inactive. Please contact your administrator to activate a company or assign you to an active company.")
        setIsLoading(false)
        return
      }
      
      // If only one active company, auto-select it
      if (activeCompanies.length === 1) {
        const company = activeCompanies[0]
        handleCompanySelect(company.id, company.name, company)
      } else if (activeCompanies.length > 1) {
        // Show company selection
        setShowCompanySelection(true)
      } else {
        // No companies, proceed anyway
        navigate("/dashboard")
      }
    } catch (err: any) {
      console.error("❌ Failed to fetch companies:", err)
      const formattedError = formatErrorMessage(err)
      // Only show error if it's a network error, otherwise proceed silently
      if (formattedError.includes('No internet connection') || formattedError.includes('network')) {
        setError(formattedError)
        setIsLoading(false)
      } else {
        // Proceed to dashboard even if companies fetch fails for other errors
        navigate("/dashboard")
      }
    } finally {
      setLoadingCompanies(false)
    }
  }

  const handleCompanySelect = (companyId: string, companyName?: string, companyObject?: any) => {
    console.log("🏢 Selecting company:", companyId, companyName)
    
    // Try to find company in the list, or use the provided company object
    let selectedCompany = companyObject || companies.find(c => c.id === companyId)
    
    // If still not found, try to fetch it or proceed if it was just auto-selected
    if (!selectedCompany) {
      // If companies array is empty or still loading, this might be an auto-select scenario
      // In this case, we'll proceed but log a warning
      console.warn("⚠️ Company not found in companies list, but proceeding with selection:", companyId)
      // Don't block - proceed with the selection if we have a valid companyId
      if (companyId) {
        try {
          localStorage.setItem("company_id", companyId)
          if (companyName) {
            localStorage.setItem("company_name", companyName)
          }
          navigate("/dashboard")
          return
        } catch (err) {
          console.error("Failed to save company to localStorage:", err)
          setError("Unable to save your company selection. Please refresh the page and try again.")
          return
        }
      } else {
        setError("Unable to access the selected company. The company may have been deactivated or you may not have permission to access it. Please contact your administrator.")
        return
      }
    }
    
    // Double-check company status if we have the company object
    if (selectedCompany) {
      const status = selectedCompany.status || 
                     selectedCompany.companySettings?.find((s: any) => s.key === 'status')?.value ||
                     'active';
      
      if (status !== 'active' && status !== 'ACTIVE') {
        setError("This company is currently inactive and cannot be accessed. Please contact your administrator to activate the company.")
        return
      }
    }
    
    try {
      localStorage.setItem("company_id", companyId)
      if (companyName) {
        localStorage.setItem("company_name", companyName)
      }
      navigate("/dashboard")
    } catch (err) {
      console.error("Failed to save company to localStorage:", err)
      setError("Unable to save your company selection. Please refresh the page and try again.")
    }
  }

  const onLoginSubmit = async (data: LoginFormData) => {
    setIsLoading(true)
    setError("")
    
    try {
      await login(data.email, data.password)
      // Fetch companies after successful login
      await fetchUserCompanies()
    } catch (err: any) {
      console.error("❌ LoginForm: Login failed:", err)
      // Format error message to be more user-friendly
      const errorMessage = err?.message || "Invalid email or password. Please try again."
      const formattedError = formatErrorMessage(err)
      setError(formattedError)
      setIsLoading(false)
    }
  }

  const handleCompanyContinue = () => {
    if (!selectedCompanyId) {
      setError("Please select a company from the list to continue.")
      return
    }
    const selectedCompany = companies.find(c => c.id === selectedCompanyId)
    if (!selectedCompany) {
      setError("Unable to access the selected company. The company may have been deactivated or you may not have permission to access it. Please contact your administrator.")
      return
    }
    
    // Validate company is active before proceeding
    const status = selectedCompany.status || 
                   selectedCompany.companySettings?.find((s: any) => s.key === 'status')?.value ||
                   'active';
    
    if (status !== 'active' && status !== 'ACTIVE') {
      setError("This company is currently inactive and cannot be accessed. Please contact your administrator to activate the company.")
      return
    }
    
    handleCompanySelect(selectedCompanyId, selectedCompany?.name)
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-cyan-50 to-blue-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-2">
          <div className="mx-auto w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
            <Brain className="w-6 h-6 text-primary-foreground" />
          </div>
          <CardTitle className="text-2xl font-bold">Welcome to UrutiIQ</CardTitle>
          <CardDescription>
            Sign in to your account to continue
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive" className="border-red-500 bg-red-50">
              <AlertDescription className="text-red-600">{error}</AlertDescription>
            </Alert>
          )}
          
          {/* Company Selection Step */}
          {showCompanySelection && (
            <div className="space-y-4">
              <div className="text-center space-y-2">
                <Building2 className="mx-auto w-8 h-8 text-primary" />
                <CardTitle className="text-xl">Select a Company</CardTitle>
                <CardDescription>
                  You have access to multiple companies. Please select one to continue.
                </CardDescription>
              </div>
              
              {loadingCompanies ? (
                <div className="text-center py-4">
                  <p className="text-muted-foreground">Loading companies...</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="company">Company</Label>
                    <Select
                      value={selectedCompanyId}
                      onValueChange={setSelectedCompanyId}
                      disabled={loadingCompanies}
                    >
                      <SelectTrigger id="company" className="w-full border border-gray-200 border-solid">
                        <SelectValue placeholder="Select a company" />
                      </SelectTrigger>
                      <SelectContent>
                        {companies.map((company) => (
                          <SelectItem key={company.id} value={company.id}>
                            {company.name || company.id}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <Button
                    onClick={handleCompanyContinue}
                    className="w-full"
                    disabled={!selectedCompanyId || loadingCompanies}
                  >
                    Continue
                  </Button>
                </div>
              )}
            </div>
          )}
          
          {/* Login Form */}
          {!showCompanySelection && (
            <form 
              onSubmit={(e) => {
                e.preventDefault()
                handleLoginSubmit(onLoginSubmit)(e)
              }} 
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    className={`pl-10 ${loginErrors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-200'}`}
                    {...registerLogin("email")}
                    disabled={isLoading}
                  />
                </div>
                {loginErrors.email && (
                  <p className="text-sm text-red-600 font-medium">{loginErrors.email.message}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    className={`pl-10 pr-10 ${loginErrors.password ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-200'}`}
                    {...registerLogin("password")}
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    disabled={isLoading}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {loginErrors.password && (
                  <p className="text-sm text-red-600 font-medium">{loginErrors.password.message}</p>
                )}
              </div>
              
              <Button
                type="submit"
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? "Signing in..." : "Sign in"}
              </Button>
              
              <div className="flex items-center justify-center">
                <Link
                  to="/auth/forgot-password"
                  className="text-sm text-primary hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
